import { useState } from 'react';
import { Alert, Button, Form, Input, Select, Table, notification } from 'antd';
import { BsPhone } from 'react-icons/bs';
import { Link } from 'react-router-dom';
import axios from '../../config/axios';
import dayjs from 'dayjs';

type DueInstallment = {
	id: number;
	amount: number;
	outstanding_amount: number;
	due_date: string;
};

type FieldType = {
	phone: string;
};

const LockScreenPayment = () => {
	const [phoneNumber, setPhoneNumber] = useState<string>('');
	const [countryCode, setCountryCode] = useState<string>('256');
	const [loading, setLoading] = useState<boolean>(false);
	const [paymentLoading, setPaymentLoading] = useState<number | null>(null);
	const [paymentAmounts, setPaymentAmounts] = useState<Record<number, number>>({});
	const [dueInstallments, setDueInstallments] = useState<DueInstallment[]>([]);
	const [alertMessage, setAlertMessage] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);

	const fetchDueInstallments = async (values: FieldType) => {
		try {
			setLoading(true);
			const fullPhoneNumber = `${countryCode}${values.phone}`;
			setPhoneNumber(fullPhoneNumber);

			const { data } = await axios.get(`/lock-screen/due-installments?phone_number=${fullPhoneNumber}`);

			if (data.err === false && data.results) {
				setDueInstallments(data.results);
				const defaults: Record<number, number> = {};
				data.results.forEach((inst: DueInstallment) => { defaults[inst.id] = inst.outstanding_amount; });
				setPaymentAmounts(defaults);
				if (data.results.length === 0) {
					setAlertMessage({
						message: 'No due installments found for this phone number',
						type: 'info',
					});
				}
			}
		} catch (error) {
			console.error('Error fetching due installments:', error);
			setAlertMessage({
				message: 'Failed to fetch due installments',
				type: 'error',
			});
		} finally {
			setLoading(false);
		}
	};

	const handlePayment = async (installmentId: number) => {
		try {
			setPaymentLoading(installmentId);

			const { data } = await axios.post(`/lock-screen/due-installments/${installmentId}/pay`, {
				phone_number: phoneNumber,
				amount: paymentAmounts[installmentId],
			});

			if (data.err === false) {
				notification.success({
					message: 'Payment prompt sent successfully',
					description: 'Please check your phone for payment instructions',
					placement: 'top',
					duration: 4,
				});
			}
		} catch (error) {
			console.error('Error processing payment:', error);
			notification.error({
				message: 'Payment failed',
				description: 'Failed to process payment request',
				placement: 'top',
				duration: 4,
			});
		} finally {
			setPaymentLoading(null);
		}
	};

	const selectBefore = (
		<Select
			defaultValue={'+256'}
			prefix={<BsPhone />}
			onChange={(value) => setCountryCode(value)}
		>
			<Select.Option value="256">+256</Select.Option>
			<Select.Option value="88">+88</Select.Option>
		</Select>
	);

	const columns = [
		{
			title: 'Installment Amount',
			dataIndex: 'amount',
			key: 'amount',
			render: (amount: number) => <span>UGX {amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>,
		},
		{
			title: 'Due Date',
			dataIndex: 'due_date',
			key: 'due_date',
			render: (date: string) => dayjs(date).format('DD MMM YYYY'),
		},
		{
			title: 'Amount to Pay',
			key: 'pay_amount',
			render: (_: unknown, record: DueInstallment) => (
				<Input
					type="number"
					suffix="UGX"
					style={{ width: 160 }}
					value={paymentAmounts[record.id] ?? record.outstanding_amount}
					onChange={(e) => {
						const val = parseFloat(e.target.value);
						setPaymentAmounts((prev) => ({ ...prev, [record.id]: isNaN(val) ? 0 : val }));
					}}
					min={500}
					max={record.outstanding_amount}
				/>
			),
		},
		{
			title: 'Action',
			key: 'action',
			render: (_: unknown, record: DueInstallment) => {
				const amt = paymentAmounts[record.id] ?? record.outstanding_amount;
				const invalid = !amt || amt < 500 || amt > record.outstanding_amount;
				return (
					<Button
						type="primary"
						onClick={() => handlePayment(record.id)}
						loading={paymentLoading === record.id}
						disabled={invalid}
					>
						Pay Now
					</Button>
				);
			},
		},
	];

	return (
		<section className="flex dark:bg-dark-bg">
			<div className="container h-dvh px-5 py-10">
				<div className="mx-auto grid h-full max-w-[800px]">
					<div className="flex w-full flex-col items-center justify-center">
						<Link to={'/'}>
							<img
								src="/images/logo.png"
								alt="logo"
								className="h-14"
							/>
						</Link>

						<div className="pt-10" />

						<h1 className="text-center text-4xl font-bold text-black-7 dark:text-white">Device Payment</h1>

						<div className="pt-4" />

						<p className="text-base font-medium text-gray-400 dark:text-gray-200">Enter your phone number to check due installments</p>

						<div className="pt-6" />

						{alertMessage && (
							<Alert
								message={alertMessage.message}
								type={alertMessage.type}
								showIcon
								className="mb-4 w-full"
							/>
						)}

						<Form
							initialValues={{ remember: true }}
							onFinish={fetchDueInstallments}
							layout="vertical"
							className="w-full pt-2"
							requiredMark={false}
						>
							<Form.Item<FieldType>
								label={<div className="pl-1 text-base font-medium text-black-7 dark:text-white">Phone Number</div>}
								name="phone"
								rules={[{ required: true, message: 'Please input your phone number!' }]}
							>
								<Input
									addonBefore={selectBefore}
									size="large"
									placeholder="Enter your phone number"
									required
									type="tel"
									className="h-11"
									onKeyDown={(e) => {
										// If the key is not a number (0-9), prevent input
										if (!/^\d$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
											e.preventDefault();
										}
									}}
									onChange={(e) => {
										// Remove any non-numeric characters
										e.target.value = e.target.value.replace(/\D/g, '');
									}}
								/>
							</Form.Item>

							<Form.Item className="mb-2">
								<Button
									htmlType="submit"
									block
									size="large"
									shape="round"
									className="h-11 font-bold"
									type="primary"
									loading={loading}
									disabled={loading}
								>
									Check Due Installments
								</Button>
							</Form.Item>
						</Form>

						{dueInstallments.length > 0 && (
							<div className="mt-6 w-full">
								<h2 className="mb-4 text-xl font-bold">Due Installments</h2>
								<Table
									dataSource={dueInstallments}
									columns={columns}
									rowKey="id"
									pagination={{ pageSize: 10 }}
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</section>
	);
};

export default LockScreenPayment;
